<script setup lang="ts">
import { computed } from "vue";

import { JobAnalysisStage } from "@/api";
import IconAtom from "@/common/atoms/IconAtom.vue";
import ButtonAtom from "@/common/atoms/ButtonAtom.vue";
import type {
  JobAnalysisEntry,
  JobAnalysisSource,
} from "@/stores/job_analysis_tracker";

const props = defineProps<{
  entry: JobAnalysisEntry;
  slow: boolean;
  agentName: string;
}>();

const emit = defineEmits<{
  (e: "dismiss"): void;
  (e: "open-entry-point"): void;
  (e: "resume"): void;
}>();

type StageRow = { stage: JobAnalysisStage; label: string };

const FETCH_LABELS: Record<Exclude<JobAnalysisSource, "file">, string> = {
  ats: "Fetching the job from your ATS",
  url: "Fetching the job page",
  voice: "Fetching the call transcript",
};

const COMMON_ROWS: StageRow[] = [
  { stage: JobAnalysisStage.ReadingJob, label: "Reading the job description" },
  {
    stage: JobAnalysisStage.GeneratingCriteria,
    label: "Creating hiring criteria",
  },
  { stage: JobAnalysisStage.Verifying, label: "Checking the criteria" },
  { stage: JobAnalysisStage.Saving, label: "Saving the position" },
];

// An upload has no fetch step, so its rows start at reading the job.
const stageRowsFor = (source: JobAnalysisSource): StageRow[] =>
  source === "file"
    ? COMMON_ROWS
    : [
        { stage: JobAnalysisStage.FetchingJob, label: FETCH_LABELS[source] },
        ...COMMON_ROWS,
      ];

const BACK_LABELS: Partial<Record<JobAnalysisSource, string>> = {
  url: "Back to Create Manually",
  file: "Back to Create Manually",
  voice: "Start a new call",
};

const isFailed = computed(
  () => props.entry.status === "failed" || props.entry.status === "timed_out",
);

const backLabel = computed(() => BACK_LABELS[props.entry.source]);

// Queued is in no row's list, so no row is current yet.
const currentIndex = computed(() =>
  stageRowsFor(props.entry.source).findIndex(
    (row) => row.stage === props.entry.stage,
  ),
);

const rows = computed(() =>
  stageRowsFor(props.entry.source).map(({ label }, index) => {
    const state =
      index < currentIndex.value
        ? "done"
        : index === currentIndex.value
          ? isFailed.value
            ? "failed"
            : "current"
          : "upcoming";
    return { label, state };
  }),
);
</script>

<template>
  <article
    class="job-analysis-toast"
    :class="{ 'job-analysis-toast--failed': isFailed }"
    :data-status="entry.status"
  >
    <img
      src="@/assets/hope/hope-with-background.png"
      alt=""
      class="job-analysis-toast__avatar"
    />
    <div class="job-analysis-toast__content">
      <p class="job-analysis-toast__title">
        {{
          entry.status === "timed_out"
            ? "Waiting for this position"
            : isFailed
              ? "Couldn't create this position"
              : `${agentName} is learning this role.`
        }}
      </p>
      <p class="job-analysis-toast__job">{{ entry.title }}</p>

      <ol class="job-analysis-toast__stages">
        <li
          v-for="(row, index) in rows"
          :key="row.label"
          class="job-analysis-toast__stage"
          :class="`job-analysis-toast__stage--${row.state}`"
          :aria-current="row.state === 'current' ? 'step' : undefined"
        >
          <span class="job-analysis-toast__marker" aria-hidden="true">
            <IconAtom v-if="row.state === 'done'" name="checkmark" :size="14" />
            <IconAtom
              v-else-if="row.state === 'failed'"
              name="alert-circle"
              :size="14"
            />
            <span
              v-else-if="row.state === 'current'"
              class="job-analysis-toast__spinner"
            />
            <span v-else class="job-analysis-toast__dot" />
          </span>
          <span>
            {{ row.label }}
            <span class="visually-hidden">
              {{
                row.state === "done"
                  ? "(done)"
                  : row.state === "failed"
                    ? "(failed)"
                    : row.state === "current"
                      ? `(step ${index + 1} of ${rows.length}, in progress)`
                      : ""
              }}
            </span>
          </span>
        </li>
      </ol>

      <p
        v-if="entry.status === 'failed' && entry.errorMessage"
        class="job-analysis-toast__error"
      >
        {{ entry.errorMessage }}
      </p>

      <ButtonAtom
        v-if="entry.status === 'timed_out'"
        class="job-analysis-toast__back"
        variant="soft-neutral"
        size="sm"
        label="Check progress"
        @click="emit('resume')"
      />
      <ButtonAtom
        v-else-if="isFailed && backLabel"
        class="job-analysis-toast__back"
        variant="soft-neutral"
        size="sm"
        :label="backLabel"
        @click="emit('open-entry-point')"
      />

      <p v-if="slow && !isFailed" class="job-analysis-toast__note">
        Taking longer than usual. You can keep working, {{ agentName }} will let
        you know when it's ready.
      </p>
    </div>
    <button
      v-if="isFailed"
      type="button"
      class="job-analysis-toast__close"
      aria-label="Dismiss"
      @click="emit('dismiss')"
    >
      <IconAtom name="close" :size="16" />
    </button>
  </article>
</template>

<style lang="scss" scoped>
@use "@/assets/styles/design-tokens" as *;

.job-analysis-toast {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: $spacing-3;
  padding: $spacing-3 $spacing-4;
  background-color: $color-surface-default;
  border: calc($spacing-1 / 4) solid $color-border-default;
  border-radius: $radius-md;
  box-shadow: $shadow-modal;
  transition:
    box-shadow 0.2s,
    border-color 0.2s;

  &--failed {
    border-color: $color-red-60;
  }
}

.job-analysis-toast__avatar {
  flex: 0 0 auto;
  display: block;
  width: $size-icon-chip;
  height: $size-icon-chip;
  border-radius: $radius-full;
  object-fit: cover;
  background-color: $color-surface-secondary;
}

.job-analysis-toast__content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: $spacing-1;
}

.job-analysis-toast__title {
  @include txt-small-plus;
  margin: 0;
  color: $color-content-default;
  overflow-wrap: anywhere;

  .job-analysis-toast--failed & {
    padding-right: $spacing-5;
  }
}

.job-analysis-toast__job {
  @include txt-xsmall;
  margin: 0 0 $spacing-1;
  color: $color-content-subtle;
  overflow-wrap: anywhere;
}

.job-analysis-toast__stages {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: $spacing-1;
}

.job-analysis-toast__stage {
  @include txt-xsmall;
  display: flex;
  align-items: center;
  gap: $spacing-2;
  color: $color-content-subtle;

  &--done {
    color: $color-content-default;
  }

  &--current {
    @include txt-xsmall-plus;
    color: $color-content-default;
  }

  &--failed {
    @include txt-xsmall-plus;
    color: $color-red-70;
  }
}

.job-analysis-toast__marker {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: $spacing-3 + $spacing-1;
  height: $spacing-3 + $spacing-1;

  .job-analysis-toast__stage--done & {
    color: $color-green-60;
  }

  .job-analysis-toast__stage--failed & {
    color: $color-red-70;
  }
}

.job-analysis-toast__dot {
  width: $spacing-1;
  height: $spacing-1;
  border-radius: $radius-full;
  background-color: $color-border-default;
}

.job-analysis-toast__spinner {
  display: inline-block;
  box-sizing: border-box;
  width: $spacing-3 + $spacing-1;
  height: $spacing-3 + $spacing-1;
  border: $focus-ring-width solid $color-border-subtle;
  border-top-color: $color-content-primary;
  border-radius: $radius-full;
  animation: job-analysis-toast-spin 0.8s linear infinite;
}

.job-analysis-toast__error {
  @include txt-small;
  margin: $spacing-2 0 0;
  color: $color-red-70;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
}

.job-analysis-toast__back {
  align-self: flex-start;
  margin-top: $spacing-1;
}

.job-analysis-toast__note {
  @include txt-xsmall;
  margin: $spacing-1 0 0;
  padding: $spacing-1-5 $spacing-2;
  background-color: $color-surface-secondary;
  border-radius: $radius-sm;
  color: $color-content-subtle;
}

.job-analysis-toast__close {
  position: absolute;
  top: $spacing-2;
  right: $spacing-2;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $spacing-1;
  background: none;
  border: none;
  border-radius: $radius-sm;
  color: $color-content-subtle;
  cursor: pointer;

  &:hover {
    background-color: $color-neutral-40;
  }

  &:focus-visible {
    @include focus-ring;
  }
}

@keyframes job-analysis-toast-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .job-analysis-toast__spinner {
    animation-duration: 2.4s;
  }
}
</style>
